// src/utils/ScrollPositionContext.tsx
// Saves and restores per-page scroll positions across navigation.
// The main scroll container in App.tsx carries id="main-scroll".
// No router dependency — works with the state-based navigation in AppShell.
import { createContext, useContext, useRef, useCallback, ReactNode } from 'react'

const SCROLL_ID = 'main-scroll'

interface ScrollContextValue {
  /** Capture current scroll position for a given page key. */
  savePosition:    (pageKey: string) => void
  /** Restore previously saved position for a page key (after DOM paint). */
  restorePosition: (pageKey: string) => void
  /** Read the saved Y value without side effects. */
  getPosition:     (pageKey: string) => number
}

const ScrollPositionContext = createContext<ScrollContextValue>({
  savePosition:    () => {},
  restorePosition: () => {},
  getPosition:     () => 0,
})

function container(): HTMLElement | null {
  return document.getElementById(SCROLL_ID)
}

export function ScrollPositionProvider({ children }: { children: ReactNode }) {
  const positions = useRef<Record<string, number>>({})

  const savePosition = useCallback((pageKey: string) => {
    const el = container()
    if (el) positions.current[pageKey] = el.scrollTop
  }, [])

  const restorePosition = useCallback((pageKey: string) => {
    const saved = positions.current[pageKey] ?? 0
    requestAnimationFrame(() => {
      const el = container()
      if (el) el.scrollTop = saved
    })
  }, [])

  const getPosition = useCallback((pageKey: string): number => {
    return positions.current[pageKey] ?? 0
  }, [])

  return (
    <ScrollPositionContext.Provider value={{ savePosition, restorePosition, getPosition }}>
      {children}
    </ScrollPositionContext.Provider>
  )
}

export function useScrollPosition() {
  return useContext(ScrollPositionContext)
}
